import { Plus } from 'lucide-react'
import { newId } from '@/lib/format'
import { useUpdate, useWedding } from '@/mock/store'
import type { Expense } from '@/mock/types'
import { categoryLabel } from '@/screens/suppliers/model'
import { useToast } from '@/shell/toaster'

/** What nearly every wedding pays for, in the order couples usually book it. */
const typical: { category: string; title: string }[] = [
  { category: 'venue', title: 'Místo konání' },
  { category: 'catering', title: 'Jídlo a pití' },
  { category: 'officiant', title: 'Oddávající' },
  { category: 'photographer', title: 'Fotograf' },
  { category: 'party-music', title: 'Hudba na hostinu' },
  { category: 'florist', title: 'Květiny' },
  { category: 'cake-sweets', title: 'Svatební dort' },
  { category: 'bride-attire', title: 'Šaty pro nevěstu' },
  { category: 'groom-attire', title: 'Oblek pro ženicha' },
  { category: 'rings', title: 'Snubní prsteny' },
  { category: 'makeup', title: 'Líčení a účes' },
  { category: 'stationery', title: 'Oznámení a tiskoviny' },
]

/**
 * One-click starters for an empty budget: each adds an item without a price
 * (filled in later). Agreed suppliers from My suppliers come first, so their
 * items are named after them. Nothing is added until the couple clicks.
 */
export function BudgetSuggestions({ onOpen }: { onOpen: (expenseId: string) => void }) {
  const data = useWedding()
  const update = useUpdate()
  const { showToast } = useToast()
  const used = new Set(data.budget.expenses.map((expense) => expense.category))
  const suppliers = data.suppliers.filter(
    (supplier) =>
      (supplier.status === 'agreed' || supplier.status === 'booked') &&
      !data.budget.expenses.some((expense) => expense.supplierId === supplier.id),
  )
  const covered = new Set([...used, ...suppliers.map((supplier) => supplier.category)])
  const options = [
    ...suppliers.map((supplier) => ({
      key: supplier.id,
      label: supplier.name,
      expense: { title: supplier.name, category: supplier.category, supplierId: supplier.id },
    })),
    ...typical
      .filter((item) => !covered.has(item.category))
      .map((item) => ({ key: item.category, label: item.title, expense: item })),
  ]
  if (options.length === 0) return null

  const add = (option: (typeof options)[number]) => {
    const expense: Expense = {
      id: newId('e'),
      amount: 0,
      estimate: true,
      payments: [],
      ...option.expense,
    }
    update((current) => ({
      ...current,
      budget: { ...current.budget, expenses: [...current.budget.expenses, expense] },
    }))
    showToast({
      title: 'Položka přidána',
      description:
        option.label === categoryLabel(expense.category)
          ? option.label
          : `${option.label} · ${categoryLabel(expense.category)}`,
      tone: 'success',
      action: {
        label: 'Doplnit cenu',
        altText: 'Otevřít přidanou položku',
        onClick: () => onOpen(expense.id),
      },
    })
  }

  return (
    <section aria-label="Na co se obvykle platí" className="space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold">Na co se obvykle platí</h2>
        <p className="text-sm text-muted-foreground">
          Klikněte na to, s čím počítáte. Cenu doplníte, až ji budete znát.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => add(option)}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-border bg-card px-4 text-xs font-semibold text-foreground/75 transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <Plus aria-hidden="true" className="size-3.5" />
            {option.label}
          </button>
        ))}
      </div>
    </section>
  )
}
